import React, { useState, useEffect } from 'react';
import { Search, UserPlus, Mail, Shield, ChevronRight, AlertCircle } from 'lucide-react';
import { Button, Card, Badge, LoadingSpinner, Modal } from '../UIComponents';
import { UserForm } from './UserForm';
import { CustomerAccessForm } from './CustomerAccessForm';
import { permissionsApi, rolesApi } from '../../lib/permissions';
import type { PortalUserFull, Role, RoleName } from '../../types/permissions';

const ROLE_COLORS: Record<RoleName, 'purple' | 'blue' | 'green' | 'slate'> = {
  superuser: 'purple',
  administrator: 'blue',
  customer_manager: 'green',
  user: 'slate',
};

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<PortalUserFull[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<PortalUserFull | null>(null);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [isAccessFormOpen, setIsAccessFormOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersData, rolesData] = await Promise.all([
        permissionsApi.getAllUsersWithRoles(),
        rolesApi.getAll(),
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUserClick = (user: PortalUserFull) => {
    setSelectedUser(user);
    setIsUserFormOpen(true);
  };

  const handleManageAccess = (user: PortalUserFull) => {
    setSelectedUser(user);
    setIsAccessFormOpen(true);
  };

  const handleUserFormClose = () => {
    setIsUserFormOpen(false);
    setSelectedUser(null);
    loadData();
  };

  const handleAccessFormClose = () => {
    setIsAccessFormOpen(false);
    setSelectedUser(null);
    loadData();
  };

  if (loading) {
    return (
      <Card className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Error Loading Users</h3>
        <p className="text-slate-500 text-sm mb-4">{error}</p>
        <Button onClick={loadData}>Try Again</Button>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <h2 className="text-lg font-bold text-slate-900">Portal Users</h2>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto -mx-6">
          <table className="w-full min-w-[700px]">
            <thead className="bg-slate-50 border-y border-slate-100">
              <tr>
                <th className="p-5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  User
                </th>
                <th className="p-5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Role
                </th>
                <th className="p-5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Customer Access
                </th>
                <th className="p-5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Status
                </th>
                <th className="p-5"></th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const highestRole = user.roles.length > 0
                  ? user.roles.reduce((prev, curr) =>
                      curr.hierarchy_level < prev.hierarchy_level ? curr : prev
                    )
                  : null;

                return (
                  <tr
                    key={user.id}
                    className="border-b border-slate-100 hover:bg-blue-50/50 cursor-pointer transition-colors group"
                    onClick={() => handleUserClick(user)}
                  >
                    <td className="p-5">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-100 text-slate-500 rounded-xl flex items-center justify-center font-bold text-lg group-hover:bg-accent group-hover:text-white transition-colors">
                          {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">
                            {user.first_name && user.last_name
                              ? `${user.first_name} ${user.last_name}`
                              : user.email.split('@')[0]}
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Mail size={12} />
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-5">
                      {highestRole ? (
                        <Badge color={ROLE_COLORS[highestRole.name as RoleName] || 'slate'}>
                          <Shield size={12} className="mr-1" />
                          {highestRole.display_name}
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-sm">No role assigned</span>
                      )}
                    </td>
                    <td className="p-5">
                      {highestRole?.name === 'superuser' ? (
                        <span className="text-purple-600 text-sm font-medium">All customers</span>
                      ) : user.customer_access.length > 0 ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">
                            {user.customer_access.length} customer{user.customer_access.length !== 1 ? 's' : ''}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleManageAccess(user);
                            }}
                            className="text-xs text-accent hover:underline font-medium"
                          >
                            Manage
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleManageAccess(user);
                          }}
                          className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1"
                        >
                          <UserPlus size={14} />
                          Grant access
                        </button>
                      )}
                    </td>
                    <td className="p-5">
                      <Badge color={user.is_active ? 'green' : 'red'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-5 text-right">
                      <div className="flex items-center justify-end text-slate-300 group-hover:text-accent transition-colors">
                        <ChevronRight size={20} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="p-12 text-center text-slate-400">
              {searchQuery
                ? 'No users match your search.'
                : 'No users have signed in yet.'}
            </div>
          )}
        </div>
      </Card>

      {/* User Form Modal */}
      <Modal
        isOpen={isUserFormOpen}
        onClose={handleUserFormClose}
        title={selectedUser ? 'Edit User' : 'Add User'}
      >
        {selectedUser && (
          <UserForm
            user={selectedUser}
            roles={roles}
            onSave={handleUserFormClose}
            onCancel={handleUserFormClose}
          />
        )}
      </Modal>

      {/* Customer Access Modal */}
      <Modal
        isOpen={isAccessFormOpen}
        onClose={handleAccessFormClose}
        title="Manage Customer Access"
      >
        {selectedUser && (
          <CustomerAccessForm
            user={selectedUser}
            onSave={handleAccessFormClose}
            onCancel={handleAccessFormClose}
          />
        )}
      </Modal>
    </>
  );
};
